import type { Meal, Recipe, IngredientRow, PantryItem, ShoppingItem, SlotMask, Rule } from './types'

// ============================================================================
// Live patch stream
//
// The backend publishes a {op, table, row|id} patch on /api/events/stream for
// every successful write to a user-owned row. The row shape matches the
// corresponding GET endpoint exactly — see CLAUDE.md "Live patch stream."
//
// Subscribers merge by id. Brief drops are tolerable; visibility-change
// refetch in App.tsx handles missed events.
// ============================================================================

export type Patch =
  | { op: 'upsert'; table: 'meal'; row: Meal }
  | { op: 'upsert'; table: 'recipe'; row: Recipe }
  | { op: 'upsert'; table: 'ingredient'; row: IngredientRow }
  | { op: 'upsert'; table: 'pantry_item'; row: PantryItem }
  | { op: 'upsert'; table: 'shopping_item'; row: ShoppingItem }
  | { op: 'upsert'; table: 'credits'; row: { balance: number } }
  | { op: 'upsert'; table: 'slot_mask'; row: SlotMask }
  | { op: 'upsert'; table: 'rule'; row: Rule }
  | { op: 'delete'; table: 'meal' | 'recipe' | 'ingredient' | 'pantry_item' | 'shopping_item' | 'rule'; id: number; note?: string }

type SSEFrame = { event: string; data: string }

async function consumeFrames(
  url: string,
  onFrame: (f: SSEFrame) => void,
  signal: AbortSignal,
): Promise<void> {
  const res = await fetch(url, { credentials: 'same-origin', signal })
  if (!res.ok || !res.body) throw new Error(`stream ${res.status}`)
  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buf = ''
  let curEvent = 'message'
  let curData = ''
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { value, done } = await reader.read()
    if (done) return
    buf += decoder.decode(value, { stream: true })
    let idx
    while ((idx = buf.indexOf('\n')) >= 0) {
      const line = buf.slice(0, idx).replace(/\r$/, '')
      buf = buf.slice(idx + 1)
      if (line === '') {
        if (curData) onFrame({ event: curEvent, data: curData })
        curEvent = 'message'
        curData = ''
        continue
      }
      const colon = line.indexOf(':')
      if (colon < 0) continue
      const field = line.slice(0, colon)
      const value = line.slice(colon + 1).replace(/^ /, '')
      if (field === 'event') curEvent = value
      else if (field === 'data') curData += (curData ? '\n' : '') + value
    }
  }
}

// subscribeEvents connects to /api/events/stream and invokes onPatch for each
// "patch" frame. Auto-reconnects with 2s backoff until signal aborts.
export async function subscribeEvents(
  onPatch: (p: Patch) => void,
  signal: AbortSignal,
): Promise<void> {
  while (!signal.aborted) {
    try {
      await consumeFrames(
        '/api/events/stream',
        (f) => {
          if (f.event !== 'patch') return
          try {
            const p = JSON.parse(f.data) as Patch
            onPatch(p)
          } catch {
            // ignore malformed frames
          }
        },
        signal,
      )
    } catch (err) {
      if (signal.aborted) return
      // network drop / server restart — reconnect after a short wait
    }
    if (signal.aborted) return
    await new Promise((r) => setTimeout(r, 2000))
  }
}
